/**
 * Front-my-End — File Import & Splitter (Goal 12)
 *
 * Client-side import for .html / .htm / .css / .js files via the Import button
 * or drag-and-drop. A combined .html document is split into its HTML, CSS, and
 * JS parts and routed to the matching editor panels. All parsing uses the
 * browser-native DOMParser — no third-party packages.
 */

// Accepted extensions and a human-readable label for guideline messages.
export const ACCEPTED = ['html', 'htm', 'css', 'js'];
export const ACCEPTED_LABEL = '.html, .css, or .js';

// Reasonable per-file ceiling for hand-authored code (512 KB).
export const MAX_BYTES = 512 * 1024;

function extensionOf(name) {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
}

export function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function readAsText(file) {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsText(file);
  });
}

// Heuristic: reject anything that is clearly not text/code.
function looksBinary(text) {
  if (text.indexOf(String.fromCharCode(0)) !== -1) return true;
  const sample = Math.min(text.length, 2000);
  let nonText = 0;
  for (let i = 0; i < sample; i++) {
    const c = text.charCodeAt(i);
    // Allow tab(9), LF(10), CR(13). Flag other control chars.
    if (c < 9 || (c > 13 && c < 32)) nonText++;
  }
  return sample > 0 && nonText > sample * 0.02;
}

// The standalone exporter wraps JS in `try { ... } catch (err) { ... }`.
// Unwrap that single guard so re-imported scripts read cleanly. Falls back
// to the original text if the shape doesn't match.
function unwrapTryCatch(js) {
  const match = js.match(/^try\s*\{\s*([\s\S]*?)\s*\}\s*catch\s*\([^)]*\)\s*\{[\s\S]*\}\s*;?\s*$/);
  return match ? match[1].trim() : js;
}

/**
 * Split a (possibly full) HTML document into { html, css, js }.
 * - All <style> contents are collected into css.
 * - All inline <script> (no src) contents are collected into js.
 * - The remaining body markup (styles/scripts stripped) becomes html.
 */
export function splitHtmlDocument(text) {
  const doc = new DOMParser().parseFromString(text, 'text/html');

  const css = Array.from(doc.querySelectorAll('style'))
    .map((el) => el.textContent.trim())
    .filter(Boolean)
    .join('\n\n');

  const js = Array.from(doc.querySelectorAll('script'))
    .filter((el) => !el.src && el.textContent.trim())
    .map((el) => unwrapTryCatch(el.textContent.trim()))
    .filter(Boolean)
    .join('\n\n');

  let html = '';
  if (doc.body) {
    const bodyClone = doc.body.cloneNode(true);
    bodyClone.querySelectorAll('style, script').forEach((n) => n.remove());
    html = bodyClone.innerHTML.trim();
  } else {
    html = text.trim();
  }

  return { html, css, js };
}

/**
 * Validate and import a FileList / array of File objects.
 * Returns { patch, errors, notes } where patch contains only the editor
 * slots that should be replaced (html/css/js). Explicit .css/.js files take
 * precedence over CSS/JS embedded inside an imported .html document.
 */
export async function importFiles(fileList) {
  const files = Array.from(fileList || []);
  const errors = [];
  const notes = [];

  if (!files.length) {
    return { patch: {}, errors: ['No files were provided.'], notes };
  }

  const parsed = [];
  for (const file of files) {
    const ext = extensionOf(file.name);
    if (!ACCEPTED.includes(ext)) {
      errors.push(`"${file.name}" is not a supported file type.`);
      continue;
    }
    if (file.size > MAX_BYTES) {
      errors.push(`"${file.name}" is ${formatBytes(file.size)}, over the ${formatBytes(MAX_BYTES)} per-file limit.`);
      continue;
    }
    let text;
    try {
      text = await readAsText(file);
    } catch (_e) {
      errors.push(`"${file.name}" could not be read.`);
      continue;
    }
    if (looksBinary(text)) {
      errors.push(`"${file.name}" doesn't look like text/code and was skipped.`);
      continue;
    }
    parsed.push({ ext, name: file.name, text });
  }

  const cssParts = [];
  const jsParts = [];
  const embeddedCss = [];
  const embeddedJs = [];
  let htmlPart = null;
  let hasExplicitCss = false;
  let hasExplicitJs = false;

  for (const item of parsed) {
    if (item.ext === 'css') {
      cssParts.push(item.text);
      hasExplicitCss = true;
      notes.push(`Loaded CSS from ${item.name}.`);
    } else if (item.ext === 'js') {
      jsParts.push(item.text);
      hasExplicitJs = true;
      notes.push(`Loaded JS from ${item.name}.`);
    } else {
      const split = splitHtmlDocument(item.text);
      htmlPart = htmlPart === null ? split.html : `${htmlPart}\n${split.html}`;
      if (split.css) embeddedCss.push(split.css);
      if (split.js) embeddedJs.push(split.js);
      notes.push(`Split ${item.name} into HTML/CSS/JS panels.`);
    }
  }

  const patch = {};
  if (htmlPart !== null) patch.html = htmlPart;
  if (hasExplicitCss) patch.css = cssParts.join('\n\n');
  else if (embeddedCss.length) patch.css = embeddedCss.join('\n\n');
  if (hasExplicitJs) patch.js = jsParts.join('\n\n');
  else if (embeddedJs.length) patch.js = embeddedJs.join('\n\n');

  if (!Object.keys(patch).length && !errors.length) {
    errors.push('Nothing importable was found in the selected file(s).');
  }

  return { patch, errors, notes };
}
